import type { CityConfig } from "../city-template/types";

export const morenoValleyConfig: CityConfig = {
  city: "Moreno Valley",
  state: "California",
  stateAbbr: "CA",
  slug: "moreno-valley",
  route: "/cities/moreno-valley",
  title: "Emergency Passport Moreno Valley, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Moreno Valley, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Riverside County and Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Moreno Valley, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Moreno Valley and Riverside County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Moreno%20Valley%20California%20panoramic%20view%20at%20golden%20hour%20with%20Box%20Springs%20Mountain%20in%20background%2C%20modern%20suburban%20neighborhoods%20with%20desert%20landscape%2C%20warm%20sunset%20light%20over%20the%20Inland%20Empire%20hills%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20Southern%20California%20suburban%20environment%20with%20warm%20amber%20and%20orange%20tones&width=1800&height=700&seq=moreno-valley-hero-001&orientation=landscape",
  heroImageAlt: "Moreno Valley California panoramic view at golden hour with Box Springs Mountain and suburban neighborhoods",
  geoPosition: "33.9375;-117.2306",
  geoPlacename: "Moreno Valley, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 60–75 minutes from Moreno Valley via I-10",
  },
  nearbyCitiesZips: [
    { name: "Moreno Valley", zips: "92551–92557" },
    { name: "Riverside", zips: "92501–92509, 92512–92519" },
    { name: "Perris", zips: "92570–92572" },
    { name: "Hemet", zips: "92542–92546" },
    { name: "Redlands", zips: "92373, 92374, 92375" },
    { name: "San Bernardino", zips: "92401–92411" },
    { name: "Corona", zips: "92879–92883" },
    { name: "Colton", zips: "92324" },
    { name: "Loma Linda", zips: "92350, 92354" },
    { name: "Grand Terrace", zips: "92313" },
    { name: "Menifee", zips: "92584, 92585" },
    { name: "Lake Elsinore", zips: "92530, 92531, 92532" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Riverside County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Riverside", route: "/cities/riverside" },
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Bernardino", route: "/cities/san-bernardino" },
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "Anaheim", route: "/cities/anaheim" },
    { name: "Ontario", route: "/cities/ontario" },
    { name: "Rancho Cucamonga", route: "/cities/rancho-cucamonga" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Moreno Valley remotely via secure courier. We do not have a walk-in office in Moreno Valley — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Riverside County travelers.",
  fpcOfficeAddress: "200 Spectrum Center Dr, Irvine, CA 92618",
  fpcOfficeDistance: "Serving all of Riverside County",
  serviceAreaIntro: "From the Sunnymead Ranch to the northern foothills, from the city center to the eastern neighborhoods — Fast Passport Center serves the entire Moreno Valley community. We regularly help travelers from Riverside, Perris, Hemet, and Redlands get their passports fast.",
  nearbyCitiesIntro: "We serve all Moreno Valley neighborhoods and nearby Inland Empire cities including Riverside, Perris, Hemet, Redlands, San Bernardino, and Corona. If you are in Riverside County or nearby San Bernardino County, we have you covered.",
  howItWorksIntro: "Most Moreno Valley travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Ontario International Airport and Los Angeles International Airport serving as the closest major hubs, Inland Empire travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Moreno Valley and Riverside County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Moreno Valley? Here are the most common ones we hear from Riverside County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Riverside County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jessica M.",
      location: "Moreno Valley, CA",
      rating: 5,
      text: "My flight to Cancun was in 2 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. Absolutely saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Robert H.",
      location: "Riverside, CA",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for Inland Empire residents.",
      service: "Passport Renewal",
    },
    {
      name: "Sandra T.",
      location: "Perris, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Marcus L.",
      location: "Redlands, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "mv-1",
      name: "Moreno Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "22265 Postal Way",
      city: "Moreno Valley",
      state: "CA",
      zip: "92553",
      phone: "(951) 242-8081",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Moreno Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "mv-2",
      name: "Riverside Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3890 Orange St",
      city: "Riverside",
      state: "CA",
      zip: "92501",
      phone: "(951) 684-3584",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Riverside",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Riverside residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "mv-3",
      name: "Perris Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "311 E 4th St",
      city: "Perris",
      state: "CA",
      zip: "92570",
      phone: "(951) 657-6084",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Perris",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "mv-4",
      name: "Hemet Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1281 S State St",
      city: "Hemet",
      state: "CA",
      zip: "92543",
      phone: "(951) 658-2824",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from downtown Hemet",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Jacinto Valley residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "mv-5",
      name: "Redlands Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "600 E Citrus Ave",
      city: "Redlands",
      state: "CA",
      zip: "92374",
      phone: "(909) 793-2793",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Redlands",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Redlands and eastern Riverside County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "mv-6",
      name: "Riverside County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2720 Gateway Dr",
      city: "Riverside",
      state: "CA",
      zip: "92507",
      phone: "(951) 486-7000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from the Riverside County administrative center",
      website: "https://www.rivcoeh.org/",
      websiteLabel: "Riverside County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Riverside County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Moreno Valley applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Riverside County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Riverside County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Moreno Valley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Moreno Valley address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Moreno Valley before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Moreno Valley for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in Moreno Valley?", a: "Fast Passport Center offers fast passport renewal in Moreno Valley in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Moreno Valley in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Moreno Valley?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Moreno Valley residents." },
    { q: "Can I expedite a child passport in Moreno Valley?", a: "Yes. Fast Passport Center expedites child passports in Moreno Valley for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Moreno Valley with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Moreno Valley?",
      a: "With Fast Passport Center's emergency courier service, Moreno Valley travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Moreno Valley?",
      a: "Popular options include the Moreno Valley Post Office (92553), Riverside Post Office (92501), Perris Post Office (92570), and Riverside County Clerk (92507). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Moreno Valley?",
      a: "Same-day passports are only available at the Los Angeles Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Perris, Hemet, or Redlands?",
      a: "We serve all of Riverside County including Perris, Hemet, Redlands, San Bernardino, Corona, and Colton. Our overnight shipping labels work from any UPS location, so your location within the Inland Empire doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Moreno Valley?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Moreno Valley?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Moreno Valley, Riverside, and Perris offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Moreno Valley?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Moreno Valley?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Riverside County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Moreno Valley", address: "24710 Sunnymead Blvd, Moreno Valley, CA 92553", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Moreno Valley", address: "12135 Day St, Moreno Valley, CA 92557", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Riverside", address: "3590 University Ave, Riverside, CA 92501", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Perris", address: "2560 N Perris Blvd, Perris, CA 92571", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Hemet", address: "1290 S State St, Hemet, CA 92543", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Redlands", address: "1785 E Lugonia Ave, Redlands, CA 92374", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Riverside", address: "10466 Magnolia Ave, Riverside, CA 92505", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Riverside", address: "12700 Day St, Riverside, CA 92553", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Moreno Valley",
      address: "14655 Meridian Pkwy, Riverside, CA 92518",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Riverside",
      address: "6200 Sycamore Canyon Blvd, Riverside, CA 92507",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Hemet",
      address: "951 S State St, Hemet, CA 92543",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — San Bernardino",
      address: "933 W Mill St, San Bernardino, CA 92410",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Bernardino County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "25 miles from Moreno Valley",
      address: "1923 E Avion St, Ontario, CA 91761",
      note: "Growing international airport for the Inland Empire. Flights to Mexico, Canada, and connections to Asia.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "70 miles from Moreno Valley",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "50 miles from Moreno Valley",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Convenient for Orange County and western Riverside County travelers.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Riverside County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Riverside County",
      url: "https://www.rivcoacr.org/",
    },
    {
      label: "Riverside County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Riverside County",
      url: "https://www.riverside.courts.ca.gov/",
    },
  ],
};